import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from "react";
import { api, setAccessToken, type Me } from "@/api/client";

type AuthState = { me: Me | null; ready: boolean; login: (email: string, password: string) => Promise<void>; loginWithToken: (token: string) => Promise<void>; logout: () => Promise<void>; reload: () => Promise<void> };
const Ctx = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [me, setMe] = useState<Me | null>(null);
  const [ready, setReady] = useState(false);

  const reload = useCallback(async () => {
    try { setMe(await api<Me>("/auth/me")); } catch { setMe(null); }
  }, []);

  useEffect(() => { reload().finally(() => setReady(true)); }, [reload]);

  const login = useCallback(async (email: string, password: string) => {
    const t = await api<{ access_token: string }>("/auth/login", { method: "POST", body: JSON.stringify({ email, password }) });
    setAccessToken(t.access_token);
    await reload();
  }, [reload]);

  const loginWithToken = useCallback(async (token: string) => { setAccessToken(token); await reload(); }, [reload]);

  const logout = useCallback(async () => {
    try { await api("/auth/logout", { method: "POST" }); } finally { setAccessToken(null); setMe(null); }
  }, []);

  return <Ctx.Provider value={{ me, ready, login, loginWithToken, logout, reload }}>{children}</Ctx.Provider>;
}

export function useAuth() {
  const v = useContext(Ctx);
  if (!v) throw new Error("useAuth outside AuthProvider");
  return v;
}
